import {
    SessionPending,
    SessionUnavailable,
    UnauthenticatedError,
    useOperator,
} from '@features/auth';
import { Brand, Sidebar, Topbar } from '@shared/layout';
import { Navigate, Outlet } from 'react-router';

/**
 * Authenticated dashboard shell. Two-zone CSS grid (brand corner + topbar over
 * sidebar + main), mirroring the portal's `(dashboard)` layout.
 *
 * This is still the **single** auth gate for the app: every screen is a child
 * route of this layout, so a page added later is protected without its author
 * doing anything. What changed with the static build (ADR 0004) is *where* the
 * check runs — `useOperator()` asks `GET /api/auth/me` from the browser instead
 * of the old Server Component verifying Atelier's cookie in-process.
 *
 * That makes this gate a **routing** decision, not a security boundary. It
 * stops an employee landing on a broken empty shell; it is not what keeps
 * anyone out. Authorisation lives entirely in `apps/api`, whose global guard
 * rejects every request without a valid session — so a hand-edited bundle that
 * skipped this check would render chrome around a screenful of 401s and no
 * data. That was true before too: the Next gate protected markup, and the API
 * protected the flags.
 *
 * It never renders a login form: the Backoffice has no login of its own
 * (NOXSCRUM-1434), it bounces to Atelier.
 */
export function DashboardLayout() {
    const { data: operator, isPending, error, refetch } = useOperator();

    if (isPending) return <SessionPending />;

    // Only a real 401 means "go and sign in". Anything else is our problem, not
    // the employee's, and saying so beats an unexplained trip to the hub.
    if (error instanceof UnauthenticatedError) return <Navigate to="/sign-in" replace />;
    if (error || !operator) return <SessionUnavailable onRetry={() => void refetch()} />;

    return (
        // `print:` unwinds the shell: no chrome, and a `main` that grows with its
        // content instead of scrolling inside a viewport-high grid, so a printed
        // screen (a migration's final report) keeps every page.
        <div className="grid h-screen grid-cols-[248px_1fr] grid-rows-[56px_minmax(0,1fr)] print:block print:h-auto">
            <div className="col-start-1 row-start-1 flex items-center border-b border-r border-line bg-surface px-[18px] print:hidden">
                <Brand />
            </div>
            <header className="col-start-2 row-start-1 border-b border-line bg-surface print:hidden">
                <Topbar operator={operator} />
            </header>
            <aside className="col-start-1 row-start-2 overflow-y-auto border-r border-line bg-surface print:hidden">
                <Sidebar operator={operator} />
            </aside>
            <main
                data-scroll-container
                className="col-start-2 row-start-2 overflow-y-auto px-8 py-7 print:overflow-visible print:p-0"
            >
                <Outlet />
            </main>
        </div>
    );
}
